<template>
  <div class="container-tbd-chart" ref="multiLineContainer">
    <div ref="chart"></div>
    <div v-if="noData" ref="noData">
      <p style="text-align: center">NO DATA</p>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import _ from 'lodash';
import { Container, createTooltip, ResizeObserver, XAxis, YAxis } from '../chart-services';

export default {
  name: 'MultiLineChart',
  props: {
    data: {
      type: Object
      // ,default: function () {
      //   return {
      //     data: [
      //       {
      //         label: 'Series 1',
      //         data: [
      //           { x: 'item1', y: 4.2 },
      //           { x: 'item2', y: 5.8 },
      //           { x: 'item3', y: 3.2 }
      //         ],
      //         trend: [
      //           { x: 'item1', y: 4.4 },
      //           { x: 'item2', y: 4.4 },
      //           { x: 'item3', y: 4.4 }
      //         ]
      //       },
      //       {
      //         label: 'Series 2',
      //         data: [
      //           { x: 'item1', y: 9.2 },
      //           { x: 'item2', y: 10.8 },
      //           { x: 'item3', y: 7.2 }
      //         ],
      //         trend: [
      //           { x: 'item1', y: 9.06 },
      //           { x: 'item2', y: 9.06 },
      //           { x: 'item3', y: 9.06 }
      //         ]
      //       }
      //     ],
      //     metadata: {
      //       x_datatype: 'datetime',
      //       y_datatype: 'number',
      //       name: 'Test Data',
      //       color: { 'Series 1': '#FF5733', 'Series 2': '3390FF' }
      //     }
      //   };
      // }
    },
    chartOptions: {
      type: Object
    }
  },
  data () {
    return {
      x: null,
      y: null,
      svg: null,
      noData: false,
      parseTime: null,
      options: {
        width: 960,
        height: 400,
        margin: { top: 20, right: 20, bottom: 50, left: 70 },
        rotateX: null, // 45,
        xScaleType: 'scaleTime', // 'scaleTime' || 'scalePoint'
        xField: 'x',
        xLabel: 'X Label',
        yField: 'y',
        yLabel: 'Y label',
        isTrend: false,
        showLegend: true,
        timeFormat: '%Y', // d3.timeParse('%Y-%m-%dT%H:%M:%S%Z'),
        decimalPoint: 2,
        yAxisMin: 0,
        dotSize: 15,
        strokeThickness: 2,
        timeLabelFormat: '%Y',
        xUnitLabel: '',
        isHideHorizontalGridLine: false,
        isHideBorderTick: false,
        isYAxisHideDecimalValue: false,
        isYAxisHideDomain: false,
        isShowLineLabel: false,
        isShowLineNodeLabel: false,
        customMaxY: undefined
      },
      resizeObserver$: null,
      selectedLineLabel: null
    };
  },
  watch: {
    selectedNode (newData, oldData) {
      console.log('The selected node changed...', newData, oldData);
    },
    data: {
      handler: function () {
        if (this.isNoContainerWidth()) { return; }
        this.initialize();
      },
      deep: true
    },
    chartOptions: function () {
      if (this.isNoContainerWidth()) { return; }
      this.initialize();
    }
  },
  mounted () {
    this.initialize();
  },
  destroyed () {
    if (this.resizeObserver$ !== null) ResizeObserver.unobserve(this.resizeObserver$);
  },
  methods: {
    formatValue: function (val) {
      const d3Format = d3.format(`,.${this.options.decimalPoint}f`);
      return d3Format(val);
    },
    widthChanged () {
      this.renderChart(this.data);
    },
    setNoDataText: function () {
      this.noData = true;
      this.$refs.chart.style.display = 'none';
    },
    hideNoDataText: function () {
      this.noData = false;
      this.$refs.chart.style.display = 'block';
    },
    customTimeFormat: function (date) {
      const d = new Date(date);
      const formatTime = d3.timeFormat(this.options.timeLabelFormat);
      return formatTime(d);
      // var timeFun = d3.timeFormat('%I %p');
      // var label = '';
      // if (timeFun(d) === '12 AM') {
      //   label = formatTime(d);
      // } else {
      //   label = timeFun(d);
      // }
      // return label;
      // var datestring = ('0' + d.getDate()).slice(-2) + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' +
      //   d.getFullYear() + ' ' + ('0' + d.getHours()).slice(-2) + ':' + ('0' + d.getMinutes()).slice(-2);
      // return datestring;
    },
    renderSvg: function (width, chartHeight, heightByData, margin) {
      const isXAxisFixed = false;
      Container.removeContainer(this.$refs.chart, { isXAxisFixed: isXAxisFixed });
      const containerInfo = Container.renderContainer(this.$refs.chart, width, chartHeight, heightByData, margin, {
        isXAxisFixed: isXAxisFixed,
        hasXAxisLabel: this.options.xLabel !== undefined
      });
      this.svg = containerInfo.$chartWrapper;
    },
    renderChart: async function (inputData) {
      const self = this;
      const jsonData = inputData;

      this.parseTime = d3.timeParse(this.options.timeFormat);
      const tooltip = createTooltip();

      this.options.width = this.$refs.multiLineContainer.clientWidth;
      const chartWidth = this.options.width - (this.options.margin.left + this.options.margin.right);
      const chartHeight = this.options.height - (this.options.margin.top + this.options.margin.bottom);
      const legendHeight = this.options.showLegend ? (jsonData.data.length * 25 + 50) : 0;

      this.renderSvg(chartWidth, (chartHeight + legendHeight), (chartHeight + legendHeight), this.options.margin);

      // Data
      let data;
      if (jsonData.data && jsonData.data.length > 0) {
        data = jsonData.data;
      }
      const metadata = jsonData.metadata;
      if (!data) return;

      const bisectDate = d3.bisector(d => d.x).left;
      let allData = [];
      for (const d in data) {
        const mappedData = _.map(data[d].data, (dd) => {
          dd.label = data[d].label;
          return dd;
        });
        allData = allData.concat.apply(allData, data[d].data);
        if (this.options.isTrend) {
          allData = allData.concat.apply(allData, data[d].trend);
        }
      }
      allData.forEach(d => {
        if (this.options.xScaleType === 'scaleTime' && !(d[this.options.xField] instanceof Date)) {
          d[this.options.xField] = this.parseTime(d[this.options.xField]);
        }
        d[this.options.yField] = +d[this.options.yField];
      });

      const bisectIndexData = _.chain(allData)
        .groupBy('x')
        .map((v, k) => {
          const obj = {
            x: new Date(k),
            data: v
          };
          return obj;
        })
        .value();

      // X Axis
      const xDomain = (this.options.xScaleType === 'scaleTime')
        ? d3.extent(allData, (d) => {
          return d[this.options.xField];
        })
        : allData.map(d => d[this.options.xField]);
      this.x = XAxis.getXScale([0, chartWidth], xDomain, { scaleType: this.options.xScaleType });
      const xAxis = XAxis.getXAxis(this.x, {
        tickSizeInner: -chartHeight,
        isHideHorizontalGridLine: this.options.isHideHorizontalGridLine,
        isHideBorderTick: this.options.isHideBorderTick,
        tickFormat: this.options.tickFormat
      });
      const isRotateX = typeof this.options.rotateX === 'number';
      const countTick = ['scaleBand', 'scalePoint'].includes(this.x.scaleType) ? this.x.domain().length : this.x.ticks().length;
      const renderXAxisOptions = {
        translate: { x: 0, y: chartHeight },
        isHideHorizontalGridLine: this.options.isHideHorizontalGridLine
        // ellipsis: { width: isRotateX ? this.options.margin.bottom : chartWidth / countTick }
      };
      if (isRotateX) { renderXAxisOptions.rotate = this.options.rotateX; }
      if (this.options.xLabel) { renderXAxisOptions.label = { text: this.options.xLabel }; }
      XAxis.renderXAxis(this.svg, xAxis, renderXAxisOptions);
      // END: X Axis

      // Y Axis
      let minY = 0;
      if (this.options.yAxisMin === 'min') {
        minY = d3.min(allData, (d) => {
          return d[this.options.yField];
        });
      } else {
        minY = Number(this.options.yAxisMin);
      }
      let maxY = d3.max(allData, (d) => {
        return d[this.options.yField];
      });
      if (this.options.customMaxY) {
        maxY = Number(this.options.customMaxY);
      }
      this.y = YAxis.getYScale([chartHeight, 0], [minY, maxY], { scaleType: 'scaleLinear' });
      const yAxis = YAxis.getYAxis(this.y);
      const renderYAxisOptions = {
        ellipsis: { width: this.options.margin.left },
        isYAxisHideDecimalValue: this.options.isYAxisHideDecimalValue,
        ticks: this.y.ticks()
      };
      if (this.options.yLabel !== undefined) {
        renderYAxisOptions.label = {
          text: this.options.yLabel,
          position: { x: -this.options.margin.left }
        };
      }

      YAxis.renderYAxis(this.svg, yAxis, renderYAxisOptions);
      // Render y grid
      const yGridOptions = {
        position: {
          x: 0
        },
        isYAxisHideDecimalValue: this.options.isYAxisHideDecimalValue
      };
      YAxis.renderYGrid(this.svg, this.y, yGridOptions);
      // END: Y Axis

      const valueline = d3.line()
        .x(d => {
          const returnVal = this.x(d[this.options.xField]) ? this.x(d[this.options.xField]) : 0;
          return returnVal;
        })
        .y(d => {
          return this.y(d[this.options.yField]);
        });

      const lineSelectedClassName = 'line-label-selected';
      for (const d in data) {
        const index = d;
        this.svg
          .append('path')
          .datum(data[d].data)
          .attr('class', 'line')
          .attr('data', function (d) {
            return d;
          })
          .style('stroke', function (d) {
            return metadata.color[data[index].label];
          })
          .style('stroke-width', function (d) {
            return self.options.strokeThickness;
          })
          .attr('data', data[index].label)
          .attr('d', valueline)
          // .on('mouseover', (event, d) => {
          //   const x0 = this.x.invert(d3.pointer(event)[0]);
          //   const i = bisectDate(d, x0, 1);

        //   const d0 = d[i - 1];
        //   const d1 = d[i];
        //   const selecteddata = x0 - d0.x > d1.x - x0 ? d1 : d0;

        //   // tooltip.show(event, {
        //   //   title: `${selecteddata.label} : ${selecteddata.x.getFullYear()}`,
        //   //   value: `Value: ${selecteddata.y}`
        //   // });

        //   const className = `.line-label-${selecteddata.label.split(' ').join('_')}`;
        //   d3.select(className).classed('line-label-selected', true);
        // })
        // .on('mousemove', (event, d) => {
        //   // const posX = d3.pointer(event)[0];
        //   // const posY = d3.pointer(event)[1];
        //   const posX = event.clientX;
        //   const posY = event.clientY;

          //   // console.log('posx', posX, event.offsetX);
          //   // console.log('posy', posY, event.offsetY);
          //   // console.log('==================');
          //   d3.select('.d3-tip').style('top', posY - 100 + 'px');
          //   d3.select('.d3-tip').style('left', posX - 100 + 'px');
          // })
          // .on('mouseout', function () {
          //   // tooltip.hide();
          //   d3.selectAll('.line-label').classed('line-label-selected', false);
          // })
          .call(tooltip.tip);

        // Trend
        if (self.options.isTrend) {
          this.svg
            .append('path')
            .datum(data[d].trend)
            .attr('class', 'line')
            .style('stroke', function (d) {
              return metadata.color[data[index].label];
            })
            .style('stroke-width', function (d) {
              return self.options.strokeThickness;
            })
            .attr('stroke-dasharray', '3, 3')
            .attr('d', valueline);
        }

        // Line label
        if (self.options.isShowLineLabel) {
          this.svg
            .append('text')
            .datum(function (d) {
              return { label: data[index].label, value: data[index].data[data[index].data.length - 1] };
            })
            .attr('transform', function (d) {
              return 'translate(' + self.x(d.value.x) + ',' + self.y(d.value.y) + ')';
            })
            .attr('class', function (d) {
              return 'line-label line-label-' + d.label.split(' ').join('_');
            })
            .attr('dx', '2em')
            .attr('dy', '.2em')
            .text(function (d) { return d.label; });
        }

        // node value label
        if (self.options.isShowLineNodeLabel) {
          _.each(data[index].data, (dnode, i) => {
            const textLabel = data[index].label;
            if (i === 0 || i === data[index].data.length - 1) {
              this.svg
                .append('text')
                .datum(function (d) {
                  return { label: textLabel, value_label: dnode.y, value: dnode };
                })
                .attr('transform', function (d) {
                  return 'translate(' + self.x(d.value.x) + ',' + self.y(d.value.y) + ')';
                })
                .attr('fill', function (d) {
                  return metadata.color[d.label];
                })
                .attr('class', 'line-node-label')
                .attr('dx', '-.5em')
                .attr('dy', '1em')
                .text(d => {
                  return this.formatValue(d.value_label);
                });
            }
          });
        }
      }

      // Hide yAxis domain
      if (self.options.isYAxisHideDomain) {
        YAxis.hideYAxisDomain(this.svg);
      }

      // Dots with tooltip
      this.svg
        .selectAll('.dot')
        .data(data)
        .enter()
        .append('g')
        .attr('class', 'dot')
        .attr('data', function (d) { return d.label; })
        .selectAll('circle')
        .data(function (d) {
          return d.data;
        })
        .enter()
        .append('circle')
        .attr('r', self.options.dotSize)
        .attr('cx', function (d) {
          const resVal = self.x(d[self.options.xField]) ? self.x(d[self.options.xField]) : 0;
          return resVal;
        })
        .attr('cy', function (d) {
          return self.y(d[self.options.yField]);
        })
        .attr('fill', function (d) {
          return 'transparent'; // metadata.color[this.parentElement.__data__.label];
        })
        .on('mouseover', (event, d) => {
          this.selectedLineLabel = d.label;
          const className = `.line-label-${this.selectedLineLabel.split(' ').join('_')}`;
          this.svg.select(className).classed(lineSelectedClassName, true);

          const xVal = this.options.xScaleType === 'scaleTime' ? d[this.options.xField].getFullYear() : d[this.options.xField];
          tooltip.show(event, {
            title: `${event.currentTarget.parentNode.__data__.label} - ${xVal}`,
            value: `Value: ${this.formatValue(d[this.options.yField])}`
          });
        })
        .on('mouseout', (event, d) => {
          this.svg.selectAll('.line-label').classed(lineSelectedClassName, false);
          tooltip.hide();
        })
        .call(tooltip.tip);

      if (self.options.showLegend) {
        const legend = this.svg
          .append('g')
          .selectAll('.legend')
          .data(data);
        const legendEnter = legend.enter();

        legendEnter
          .append('g')
          .attr('class', 'legend')
          .append('rect')
          .attr('width', 19)
          .attr('height', 19)
          .attr('fill', function (d) {
            return metadata.color[d.label];
          })
          .attr('x', 0)
          .attr('y', function (d) {
            return data.map(e => e.label).indexOf(d.label) * 20 + (chartHeight + 60);
          });

        legendEnter
          .append('text')
          .attr('x', 25)
          .attr('y', ('y', function (d) {
            return data.map(e => e.label).indexOf(d.label) * 20 + (chartHeight + 60 + 10);
          }))
          .attr('dy', '0.32em')
          .text(function (d) {
            return d.label;
          })
          .attr('fill', '#333')
          .attr('font-family', 'sans-serif')
          .attr('font-size', 10)
          .attr('text-anchor', 'start');
      }

      // mouse over
      // this.svg
      //   .append('rect')
      //   .attr('class', 'overlay')
      //   .attr('width', chartWidth)
      //   .attr('height', chartHeight)
      //   .on('mousemove', (event, d) => {
      //     const x0 = this.x.invert(d3.pointer(event)[0]);
      //     const i = bisectDate(bisectIndexData, x0, 1);
      //     console.log('d', x0, i);

      //     const d0 = bisectIndexData[i - 1];
      //     const d1 = bisectIndexData[i];
      //     const selecteddata = x0 - d0.x > d1.x - x0 ? d1 : d0;
      //     console.log('data', selecteddata);

      //     tooltip.show(event, {
      //       title: `${selecteddata.x}`,
      //       value: `Value: test`
      //     });
      //   })
      //   .on('mouseout', function () {
      //     tooltip.hide();
      //   })
      //   .call(tooltip.tip);
    },
    isNoContainerWidth: function () {
      return this.$el && this.$el.offsetWidth <= 0;
    },
    initialize () {
      if (this.data === undefined) {
        this.setNoDataText();
        return;
      }
      if (this.chartOptions) _.merge(this.options, this.chartOptions);
      this.renderChart(this.data);

      const resizeObserverOptions = { onlyWidth: true, skipFirst: true };
      if (this.isNoContainerWidth()) {
        // If width less than zero, it will not trigger in the first time
        resizeObserverOptions.skipFirst = false;
      }
      this.resizeObserver$ = ResizeObserver.observe(this.$el, () => {
        if (this.isNoContainerWidth()) { return; }
        this.widthChanged();
      }, resizeObserverOptions);
    }
  }
};
</script>

<style>
@import "../chart-services/css/main.css";

.line {
  fill: none;
  stroke: steelblue;
  /*stroke-width: 2px;*/
}
.line-node-label {
  font-size: 12px;
  font-family: 'Roboto';
}
.line-label {
  font-size: 12px;
  fill: #8f8f8f;
  font-family: 'Roboto';
  font-weight: 500;
}
.line-label-selected {
  fill: #1890ff !important;
  font-weight: bold !important;
}
</style>
